import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useMe,
  useUserProducts,
  useUserEventsAndMaterials,
} from "@/data/user.ts";
import dayjs, { Dayjs } from "dayjs";


interface Text {
  text: string;
  subText: string;
}

interface IReturn {
  text: Text;
  daysLeft: number;
  hasStarted: boolean;
  isOneToOne: boolean;
  kickoffDate: string | undefined;
  kickDaysLeft: number;
  kickMinus18days: Dayjs;
  showAvailabilities: boolean;
  showStartCourseBadge: boolean;
}

const TODAY = dayjs();

const defaultText = {
  text: "",
  subText: "",
};

export default function useStartCourse(): IReturn {
  const { t } = useTranslation();
  const { me } = useMe();
  const { products } = useUserProducts();


  const { eventMaterials } = useUserEventsAndMaterials();

  const [text, setText] = useState<Text>(defaultText);
  const [isOldUser, setIsOldUser] = useState(false);
  const [isOneToOne, setIsOneToOne] = useState(false);

  const kickoffDateFormatted = me?.item.kickoff_date
    .split("/")
    .reverse()
    .join("-");

  const kickoffDate = dayjs(kickoffDateFormatted);
  const daysLeft = kickoffDate.clone().diff(TODAY, "days", true);
  const kickMinus18days = kickoffDate.clone().subtract(18, "day");

  const kickDaysLeft = dayjs(me?.item?.next_kickoff_date).diff(TODAY, "days");

  useEffect(() => {
    if (products && products.items && products.items.length > 0) {
      setIsOneToOne(
        !!products?.items?.[0]?.course_info?.course_name
          ?.toLowerCase()
          ?.includes("lezioni individuali"),
      );
    }

    if (eventMaterials && eventMaterials?.item.events.length > 0) {
      setIsOldUser(
        dayjs(eventMaterials.item.events[0].data).isBefore(kickoffDate),
      );
    }

    if (daysLeft > 1) {
      setText({
        text: t("start_course.course_will_start_on"),
        subText: kickoffDateFormatted ? kickoffDate.format("DD MMMM") : "",
      });
    } else if (daysLeft >= 0 && daysLeft <= 1) {
      setText({
        text: t("start_course.course_will_start"),
        subText: t("start_course.tomorrow"),
      });
    } else if (daysLeft < 0 && Math.floor(Math.abs(daysLeft)) <= 7) {
      setText({
        text: t("start_course.course_has_started_on"),
        subText: t("start_course.this_week"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    products,
    daysLeft,
    // kickoffDate,
    eventMaterials,
    kickoffDateFormatted,
  ]);

  const showStartCourseBadge =
    !isOldUser &&
    (daysLeft > 0 || (daysLeft < 0 && Math.floor(Math.abs(daysLeft)) <= 7));

  const hasStarted = daysLeft <= 2 || isOldUser;

  const showAvailabilities =
    TODAY.isAfter(kickMinus18days) ||
    TODAY.isSame(kickMinus18days) ||
    isOldUser;

  return {
    text,
    hasStarted,
    isOneToOne,
    kickDaysLeft,
    kickMinus18days,
    showAvailabilities,
    showStartCourseBadge,
    daysLeft: Math.floor(daysLeft),
    kickoffDate: kickoffDateFormatted,
  };
}
